import { useState } from "react";
import { MessageSquare, Clock } from "lucide-react";
import { SITDOWN_PILLARS } from "@/components/sitdown/constants";
import { PillarCard, TopicList, SitDownSummary } from "@/components/sitdown/SitDownParts";

const SitDownIntro = () => (
  <div className="max-w-3xl">
    <div className="text-xs tracking-tight text-[var(--accent)] mb-3 flex items-center gap-2">
      <MessageSquare className="w-3.5 h-3.5" /> Sit-Down Conversations
    </div>
    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#0A192F] leading-[0.95] tracking-tight">
      Pick a category. <br />
      <span className="text-[var(--accent)]">Name the topics.</span> Sit down with Vic.
    </h2>
    <p className="mt-6 text-base md:text-lg text-slate-700 leading-relaxed">
      ATFQ books long-form, data-driven deep dives with experts, officials, and listeners.
      Choose the category you want prioritized, check the specific topics, then tell us
      your question and the format you want.
    </p>
    <div className="mt-6 flex flex-wrap items-center gap-6 text-xs text-slate-500">
      <span className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-[var(--accent)]" />~ 3 minutes
      </span>
      <span>· Anonymous option</span>
      <span>· Opens a pre-filled Google Form</span>
    </div>
  </div>
);

export const SitDownSurvey = () => {
  const [pillarKey, setPillarKey] = useState(null);
  const [topics, setTopics] = useState([]);
  const pillar = SITDOWN_PILLARS.find((p) => p.key === pillarKey);

  const selectPillar = (key) => {
    setPillarKey(key);
    setTopics([]);
  };
  const toggleTopic = (t) =>
    setTopics((ts) => (ts.includes(t) ? ts.filter((x) => x !== t) : [...ts, t]));

  return (
    <section
      id="survey"
      data-testid="sitdown-section"
      className="relative bg-white py-24 md:py-32 border-y border-slate-200"
    >
      <div className="px-6 md:px-12 lg:px-20 max-w-[1400px] mx-auto">
        <SitDownIntro />

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {SITDOWN_PILLARS.map((p) => (
            <PillarCard key={p.key} pillar={p} active={p.key === pillarKey} onSelect={selectPillar} />
          ))}
        </div>

        {pillar ? (
          <div
            data-testid="sitdown-step-topics"
            className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 border-t border-slate-200 pt-12"
          >
            <div className="lg:col-span-7">
              <div className="text-xs tracking-tight text-[var(--accent)] mb-3">
                Step 2 · Topics within {pillar.short}
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-[#0A192F] tracking-tight mb-6">
                Which should we investigate? Pick as many as you like.
              </h3>
              <TopicList pillar={pillar} selected={topics} onToggle={toggleTopic} />
            </div>
            <div className="lg:col-span-5">
              <SitDownSummary pillar={pillar} topics={topics} />
            </div>
          </div>
        ) : (
          <p data-testid="sitdown-step-hint" className="mt-10 text-sm text-slate-500">
            Step 1 · Select a category above to see its topics.
          </p>
        )}
      </div>
    </section>
  );
};

export default SitDownSurvey;
